import { useEffect, useMemo, useState } from 'react';
import type { HomeBannerAdminView } from '../../types/cms';
import { isBannerLive } from '../lib/bannerSchedule';

// C-2: 스토어프론트 히어로 캐러셀(데스크톱 peek)을 백오피스용 읽기 전용 미리보기로 이식.
// storefront HeroBanner.tsx 의 핵심(중앙 타일 + 좌우 peek + 텍스트 + 진행바)을 재현.
// iframe 미사용, 어드민 배너 데이터로 렌더. 클릭/드래그 없는 자동 회전 프리뷰.
// 백오피스는 styled-components 미사용 → Tailwind className + CSS 변수 inline style 사용.

const CENTER_TILE = 240;
const SIDE_TILE = 168;
const TILE_GAP = 24;
const AUTO_ROTATE_MS = 4000;

const pad2 = (n: number): string => String(n).padStart(2, '0');

const tileTransform = (offset: number): string => {
  if (offset === 0) return 'translate(-50%, -50%)';
  const shift =
    offset < 0
      ? -(CENTER_TILE / 2 + TILE_GAP + SIDE_TILE / 2)
      : CENTER_TILE / 2 + TILE_GAP + SIDE_TILE / 2;
  return `translate(calc(-50% + ${shift}px), -50%)`;
};

export interface HeroBannerPreviewProps {
  banners: ReadonlyArray<HomeBannerAdminView>;
}

export const HeroBannerPreview = ({ banners }: HeroBannerPreviewProps) => {
  // 스토어프론트와 동일하게 **활성 + 노출 예약 윈도우 안**인 배너만, 정렬 순서대로 노출.
  //   isActive 만 보면 기간이 끝난 배너까지 세어 실제 노출 수와 어긋난다(활성 69건 중 노출 12건인데
  //   미리보기 카운터가 69로 표시되던 문제). 판정 규칙은 BE HomeBanner.isWithinSchedule 과 동일.
  const slides = useMemo(
    () => {
      const now = Date.now();
      return banners
        .filter((b) => isBannerLive(b, now))
        .slice()
        .sort((a, b) => a.sortOrder - b.sortOrder);
    },
    [banners],
  );
  const total = slides.length;
  // 활성이지만 기간을 벗어나 제외된 수 — 비어 있을 때 이유를 설명하는 데 쓴다.
  const activeButOutOfWindow = useMemo(
    () => banners.filter((b) => b.isActive).length - total,
    [banners, total],
  );
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    if (activeIndex >= total) setActiveIndex(0);
  }, [total, activeIndex]);

  useEffect(() => {
    if (total <= 1) return undefined;
    const timer = setInterval(() => setActiveIndex((prev) => (prev + 1) % total), AUTO_ROTATE_MS);
    return () => clearInterval(timer);
  }, [total]);

  const frameClass = 'relative w-full overflow-hidden rounded-xl';
  const frameStyle = {
    height: 420,
    border: '1px solid var(--divider-strong)',
    background: 'var(--surface-0)',
  } as const;

  if (total === 0) {
    return (
      <div className={`flex items-center justify-center ${frameClass}`} style={frameStyle}>
        <span className="px-6 text-center text-sm" style={{ color: 'var(--content-muted)' }}>
          {activeButOutOfWindow > 0
            ? `활성 배너 ${activeButOutOfWindow}건이 있지만 모두 노출 기간을 벗어나 지금은 노출되지 않습니다. 목록에서 노출 기간을 확인하세요.`
            : '활성화된 배너가 없습니다. 배너를 등록하면 미리보기가 표시됩니다.'}
        </span>
      </div>
    );
  }

  const active = slides[activeIndex];
  if (!active) return null;
  const progress = total <= 1 ? 100 : ((activeIndex + 1) / total) * 100;

  return (
    <div className={frameClass} style={frameStyle} aria-label="홈 히어로 배너 미리보기" data-testid="hero-banner-preview">
      <div
        aria-hidden="true"
        className="absolute"
        style={{
          inset: -80,
          backgroundImage: `url(${active.imageUrl})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          filter: 'blur(36px) saturate(120%)',
          opacity: 0.5,
          zIndex: 0,
          transition: 'background-image 500ms ease',
        }}
      />
      <div
        aria-hidden="true"
        className="absolute inset-0"
        style={{
          zIndex: 1,
          background:
            'linear-gradient(180deg, rgba(255,255,255,0.2) 0%, rgba(255,255,255,0) 50%, rgba(255,255,255,0.55) 100%)',
        }}
      />

      <div className="relative flex h-full flex-col pt-7" style={{ zIndex: 2 }}>
        <div className="flex flex-col items-center gap-2 text-center">
          <p
            className="m-0 text-xs font-bold uppercase tracking-wide"
            style={{ color: 'var(--accent-default)' }}
          >
            {active.eyebrow}
          </p>
          <h3
            className="m-0 break-keep text-lg font-bold leading-snug"
            style={{ color: 'var(--content-strong)' }}
          >
            {active.albumTitle}
          </h3>
          <p
            className="m-0 text-xs font-medium"
            style={{ color: active.taglineColor || 'var(--content-muted)' }}
            data-testid="hero-preview-tagline"
          >
            {active.tagline}
          </p>
        </div>

        <div
          className="relative mt-5 flex items-center justify-center"
          style={{ width: '100%', height: CENTER_TILE }}
        >
          {slides.map((slide, idx) => {
            let offset = idx - activeIndex;
            const half = Math.floor(total / 2);
            if (offset > half) offset -= total;
            if (offset < -half) offset += total;
            if (Math.abs(offset) > 1) return null;
            const isCenter = offset === 0;
            const size = isCenter ? CENTER_TILE : SIDE_TILE;
            return (
              <div
                key={slide.id}
                aria-hidden={!isCenter}
                className="absolute overflow-hidden rounded-md"
                style={{
                  top: '50%',
                  left: '50%',
                  width: size,
                  height: size,
                  transform: tileTransform(offset),
                  background: 'var(--surface-1)',
                  boxShadow: isCenter
                    ? '0 4px 8px rgba(0,0,0,0.18)'
                    : '0 2px 4px rgba(0,0,0,0.08)',
                  transition: 'width 400ms ease, height 400ms ease, transform 400ms ease, box-shadow 400ms ease',
                }}
              >
                {slide.imageUrl ? (
                  <img
                    src={slide.imageUrl}
                    alt={`${slide.artistName} - ${slide.albumTitle}`}
                    className="h-full w-full object-cover"
                    loading="lazy"
                  />
                ) : (
                  <div
                    className="flex h-full w-full items-center justify-center text-[11px]"
                    style={{ color: 'var(--content-soft)' }}
                  >
                    이미지 없음
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="mt-auto flex items-center gap-3 px-6 py-4">
          <div
            className="relative h-[3px] flex-1 overflow-hidden rounded-sm"
            style={{ background: 'rgba(120,120,120,0.3)' }}
          >
            <div
              className="absolute inset-y-0 left-0 rounded-sm"
              style={{
                width: `${progress}%`,
                background: 'var(--accent-default)',
                transition: 'width 400ms ease',
              }}
            />
          </div>
          <div className="flex items-center gap-1.5 text-[13px] font-bold" style={{ color: 'var(--content-muted)' }}>
            <span style={{ color: 'var(--accent-default)' }}>{pad2(activeIndex + 1)}</span>
            <span aria-hidden="true">/</span>
            <span>{pad2(total)}</span>
          </div>
        </div>
      </div>
    </div>
  );
};
